/** Интервалы поллинга. Query сам останавливает их, когда вкладка неактивна. */
export const POLLING = {
  /**
   * Счётчики шапки (`getBadges`). Шапка висит на КАЖДОМ экране, поэтому это единственный
   * запрос, который идёт постоянно, где бы пользователь ни находился. Ручка нарочно дешёвая —
   * три числа; полная лента уведомлений грузится только при открытии поповера.
   */
  badges: 30_000,

  /*
   * ⚠️ `notifications` здесь НЕТ намеренно — ленту уведомлений опрашивать НЕЛЬЗЯ. Открытие поповера
   * помечает всё прочитанным (так велит контракт), поэтому повторный запрос ленты возвращает
   * `unreadCount: 0`: поллинг гасил счётчик «N новых» и подсветку непрочитанных прямо под курсором у
   * читающего — проверено живым прогоном. Свежесть даёт `staleTime: 0` при открытии поповера, точку
   * на колокольчике обновляет `badges`.
   *
   * ⚠️ Пока сервиса уведомлений нет, мутация снята и лента пуста — но строку сюда возвращать всё
   * равно нельзя: с сервисом вернётся и мутация, а с ней та же ловушка.
   */

  inbox: 10_000,
  /**
   * Открытый тред брони — лента И шапка (черновик ИИ, статус). 5с: оператор ждёт ответа гостя
   * в реальном времени, и десятисекундная пауза ощущается как «висит». Это ОДИН открытый тред,
   * а не список, поэтому чаще inbox-списка — допустимо. Строки списка обновляет `inbox` (10с).
   */
  conversation: 5_000,
  tasks: 20_000,
  chess: 60_000,
  dashboard: 60_000,
  channelEvents: 30_000,
  /**
   * Список каналов — только пока в нём есть незавершённый (`pending`): его вход может довершиться и
   * без окна — событием у Connect. Повторный вход в подключённый канал так не отслеживается: его
   * итог список узнает при следующем чтении. Брошенный незавершённый канал держит опрос, пока
   * открыт экран, — раз в 10 с; у шлюза каждое такое чтение — ещё и живое чтение его сессии.
   */
  pendingChannels: 10_000,
  qrConnect: 2_000,
  /**
   * Применение загрузки базы гостей — интервал короткий, как у QR-подключения. ⚠️ `CommitImport`
   * СРАЗУ ВОЗВРАЩАЕТСЯ и применяет фоном (`prima-clients`, `import_server.go`, `CommitImport`; их
   * замер — 3 мин 54 с на 14 610 строк), поэтому ход виден ТОЛЬКО через этот опрос: счётчики сервер
   * обновляет каждые 200 строк.
   *
   * ⚠️ Опрос идёт, только пока применение ИДЁТ: `status === 'committing'` И без причины сбоя (правило
   * — `clientImportFailed`). В «идёт» статус переводит ответ commit'а, а до него — оптимистичная
   * запись `onMutate` у `useCommitClientImport`.
   */
  clientImport: 2_000,

  /*
   * ⚠️ `bookings` здесь НЕТ намеренно: список курсорный, а `refetchInterval` на infinite-запросе
   * перезапрашивает КАЖДУЮ уже загруженную страницу, а не только последнюю — пролистал десять
   * страниц, и каждый тик уходит десять запросов. Плюс строки переставлялись бы под курсором у
   * читающего. Список броней — экран для просмотра, ему хватает `refetchOnWindowFocus`.
   */
} as const

/** Сколько данные считаются свежими. */
export const STALE = {
  /** Справочники: каталог сценариев, объекты шахматки, сервисы платформы. */
  reference: 600_000,
  /** Чаты и всё, что меняется постоянно. */
  live: 0,
  /**
   * Очередь задач и flow-slice: 10с свежести (§6). Операционный экран, но лишние рефетчи на
   * фокус/маунт ни к чему — поллинг всё равно идёт по POLLING.tasks (20с).
   */
  tasks: 10_000,
  /** Счётчики шапки: 15с свежести при поллинге 30с (§6). */
  badges: 15_000,
  chess: 30_000,
  dashboard: 30_000,
  bookings: 30_000,
  studio: 60_000,
  scenarioCatalog: 600_000,
  scenarios: 60_000,
  scenarioDetail: 60_000,
  scenarioTemplates: 600_000,
  policy: 60_000,
  contentList: 60_000,
  contentCatalog: 600_000,
  contentDetail: 60_000,
  contentPreview: 60_000,
  apartments: 60_000,
  apartmentDetail: 60_000,
  apartmentVariableMatrix: 60_000,
  /*
   * Роли-замки. Меняются, когда менеджер ЗАДАЁТ ЗНАЧЕНИЕ секрета (заводить и снимать секреты в
   * приложении нельзя — набор фиксирован квартирой), то есть в другом разделе и редко; в редакторе
   * сценария список нужен на каждое открытие шага.
   *
   * ⚠️ Пять минут не «на всякий случай»: запись значения СБРАСЫВАЕТ этот запрос явно
   * (`useSaveApartmentSecret`), поэтому срок отвечает только за правки из другой вкладки.
   */
  gatedRoles: 300_000,
  /**
   * Карточка гостя. Одна и та же в брони и в разделе «Гости», поэтому свежесть общая: открыл гостя
   * в списке, открыл его бронь — второй запрос не уходит.
   */
  clientDetail: 60_000,
  /** База гостей: список меняется от новых броней, а не под курсором — минуты свежести хватает. */
  clients: 60_000,
  channels: 60_000,
  /** Боты компании меняются вручную и редко — на карточке брони это фон, а не живое состояние. */
  botLinks: 300_000,
  channelCatalog: 600_000,
  channelEvents: 0,
  channelCascade: 60_000,
  concierge: 60_000,
  /** Столько бронь считается свежей после префетча по наведению. */
  bookingDetail: 30_000,
  /**
   * Экран гостя — ЖИВОЕ состояние: что у него открыто прямо сейчас. Держать его свежим полминуты,
   * как карточку, значило бы показывать вчерашнюю правду в поповере, который открывают именно
   * затем, чтобы узнать текущую. Перечитываем на каждое открытие.
   */
  guestScreen: 0,
} as const
